import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { BaseDialog } from '@/components/shared/base-dialog';
import { PathType } from '@/data/learning-path/interfaces';
import { useCreate } from '@/data/learning-path/path-management';
import { useUser } from '@/data/user';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { isPathsManager } from '@/packages/learning-path/pages/path-management/utils/is-paths-manager';

const schema = z.object({
  title: z
    .string({ message: 'Título obrigatório' })
    .min(1, 'Título obrigatório'),
  description: z
    .string({ message: 'Descrição obrigatória' })
    .min(1, 'Descrição obrigatória'),
  path_type: z.enum([PathType.Company, PathType.Global], {
    message: 'Tipo de trilha obrigatório',
  }),
});

type FormData = z.infer<typeof schema>;

interface CreateLearningPathDialogProps {
  onClose: () => void;
}

export const CreateLearningPathDialog = ({
  onClose,
}: CreateLearningPathDialogProps) => {
  const { mutate, isPending } = useCreate();
  const { user } = useUser();
  const { bootstrap } = useBootstrapData();

  const { control, handleSubmit } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      path_type: PathType.Company,
    },
  });
  const navigate = useNavigate();
  const manager = user && isPathsManager(user);

  const onSubmit = ({ title, description, path_type }: FormData) => {
    mutate(
      {
        learning_path: {
          title,
          description,
          path_type,
          external_company_id: bootstrap?.info?.colaborador?.empresa_id ?? 0,
        },
      },
      {
        onSuccess: (resp) => {
          if (resp?.learning_path) {
            navigate(`/empresa/gestao-de-trilhas/${resp.learning_path.id}`);
            onClose();
          }
        },
      }
    );
  };

  return (
    <BaseDialog
      open
      fullWidth
      title="Nova trilha"
      actionsComponent={
        <>
          <Button variant="text" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={isPending}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            Criar
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Stack spacing={3}>
        <Controller
          control={control}
          name="title"
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <FormLabel htmlFor="title">Título</FormLabel>
              <TextField
                {...field}
                error={!!error}
                id="title"
                placeholder="Ex.: Onboarding na empresa, Compliance, etc."
                variant="outlined"
                helperText={error?.message ?? ''}
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <FormLabel htmlFor="description">Descrição</FormLabel>
              <TextField
                {...field}
                error={!!error}
                id="description"
                placeholder="Escreva um resumo do que esta trilha se trata"
                variant="outlined"
                helperText={error?.message ?? ''}
              />
            </FormControl>
          )}
        />
        {manager && (
          <Controller
            control={control}
            name="path_type"
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <FormLabel htmlFor="path_type">Tipo da trilha</FormLabel>
                <Select
                  {...field}
                  id="path_type"
                  variant="outlined"
                  defaultValue={PathType.Company}
                  inputProps={{ 'aria-label': 'Tipo da trilha' }}
                >
                  <MenuItem value={PathType.Company}>Galena</MenuItem>
                  <MenuItem value={PathType.Global}>Global</MenuItem>
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        )}
      </Stack>
    </BaseDialog>
  );
};
